import React from 'react';

import {CodeHighlightNode, CodeNode} from '@lexical/code-core';
import {registerCodeHighlighting} from '@lexical/code-prism';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {PlainTextPlugin} from '@lexical/react/LexicalPlainTextPlugin';
import {
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  INSERT_LINE_BREAK_COMMAND,
  PASTE_COMMAND,
} from 'lexical';
import {UpdateListenerPayload} from 'lexical/LexicalEditor';
import noop from 'lodash/noop';
import 'prismjs/components/prism-json';

import {newFormField} from '@edna/components/FormField';

import * as S from './style';
import {
  $buildEditorFromText,
  $createJsonNode,
  $serializeEditorToText,
  getNewLineText,
} from './utils';

const theme = {
  code: 'codeBlock',
  codeHighlight: {
    atrule: 'token atrule',
    keyword: 'token keyword',
    regex: 'token regex',
    variable: 'token variable',
    comment: 'token comment',
    string: 'token string',
    number: 'token number',
    punctuation: 'token punctuation',
    function: 'token function',
    operator: 'token operator',
    property: 'token property',
  },
};

const ROWS_NUMBER = 10;
const SYNC_TAG = 'external-sync';

const CodeHighlightPlugin = () => {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    return registerCodeHighlighting(editor);
  }, [editor]);

  return null;
};

type TProps = {
  className?: string;
  value?: string | null;
  id?: string;
  onChange?: (value: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  disabled?: boolean;
  readOnly?: boolean;
  isInvalid?: boolean;
  rows?: number;
};

export const JsonContent = ({
  value = '',
  onBlur,
  onChange,
  onFocus,
  id: labelId,
  disabled,
  readOnly,
  isInvalid,
  rows = ROWS_NUMBER,
  className,
}: TProps) => {
  const [editor] = useLexicalComposerContext();

  const serializedRef = React.useRef<string>(value ?? '');
  const valueRef = React.useRef<string>(value ?? '');

  React.useEffect(
    () =>
      editor.registerUpdateListener(
        ({editorState, tags, dirtyElements, dirtyLeaves}: UpdateListenerPayload) => {
          const text = editorState.read($serializeEditorToText);

          serializedRef.current = text;

          if (tags.has(SYNC_TAG) || (dirtyElements.size === 0 && dirtyLeaves.size === 0)) {
            return;
          }

          const normalized = text.trim() ?? '';

          if (normalized !== valueRef.current) {
            onChange?.(normalized);
          }
        },
      ),
    [editor, onChange],
  );

  React.useEffect(() => {
    const unregisterPaste = editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        event.preventDefault();

        const data = event.clipboardData?.getData('text/plain');

        if (!data) {
          return true;
        }

        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            selection.removeText();
            $insertNodes([$createJsonNode(data, true)]);

            return;
          }

          $getRoot().selectEnd();
          $insertNodes([$createJsonNode(data, true)]);

          return;
        });

        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    const unregisterLineBreak = editor.registerCommand(
      INSERT_LINE_BREAK_COMMAND,
      () => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          selection.insertParagraph();
          selection.insertText(getNewLineText());
        }

        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    return () => {
      unregisterPaste();
      unregisterLineBreak();
    };
  }, [editor]);

  React.useEffect(() => {
    editor.setEditable(!(disabled || readOnly));
  }, [editor, disabled, readOnly]);

  React.useEffect(() => {
    const external = value ?? '';

    if (external !== serializedRef.current.trim()) {
      editor.update(() => $buildEditorFromText(value), {tag: SYNC_TAG});
    }
  }, [value]);

  return (
    <>
      <S.EditorContainer
        className={className}
        disabled={disabled}
        readOnly={readOnly}
        isInvalid={isInvalid}
        rows={rows}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <PlainTextPlugin
          contentEditable={
            <ContentEditable className="editor-content" aria-labelledby={labelId} spellCheck />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
      </S.EditorContainer>
      <CodeHighlightPlugin />
      <HistoryPlugin />
    </>
  );
};

export const JsonArea = (props: TProps) => {
  const [initialConfig] = React.useState(() => ({
    namespace: 'JsonArea',
    onError: noop,
    theme,
    nodes: [CodeNode, CodeHighlightNode],
    editorState: () => $buildEditorFromText(props.value),
  }));

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <JsonContent {...props} />
    </LexicalComposer>
  );
};

export const JsonAreaField = newFormField<TProps>(JsonArea, {
  isLabelledBy: true,
});
